import axiosInstance from "src/axios";
import type {
  ActiveCashbackResponse,
  OrderCashbackView,
} from "src/utils/cashback";

const BASE = process.env.NEXT_PUBLIC_BASE_URL ?? "";

export async function fetchActiveCashback(
  currency: string,
): Promise<ActiveCashbackResponse> {
  try {
    const res = await fetch(
      `${BASE}/cashback/active?currency=${encodeURIComponent(currency.split("-")[0].toLowerCase())}`,
    );
    if (!res.ok) return { active: false };
    return (await res.json()) as ActiveCashbackResponse;
  } catch {
    return { active: false };
  }
}

/**
 * GET /order/:id/cashback sits behind OrderOwnerGuard: a guest proves
 * ownership with x-guest-token, sent here explicitly (pre-flight M1).
 * `paymentOption` is the payment choice on screen right now; the server
 * uses it only to word an unpaid cart's preview (pre-flight M6).
 */
export async function fetchOrderCashback(
  orderId: string,
  guestToken: string | null | undefined,
  paymentOption?: string,
): Promise<OrderCashbackView> {
  const res = await axiosInstance.get(`/order/${orderId}/cashback`, {
    headers: guestToken ? { "x-guest-token": guestToken } : undefined,
    params: paymentOption ? { paymentOption } : undefined,
  });
  return res.data as OrderCashbackView;
}

// ---------------------------------------------------------------------------
// Admin (/cashback/admin/*): reads are any admin, writes superadmin. Typed by
// hand from the backend's cashback-award.dto.ts / cashback-promotion.dto.ts.
// ---------------------------------------------------------------------------

export type CashbackAwardStatus = "PENDING" | "GRANTED" | "CANCELED" | "VOIDED";

export type AdminCashbackPromotionTotals = {
  promotionId: string;
  currency: string;
  awards: number;
  orders: number;
  qualifyingCents: number;
  averageQualifyingCents: number;
  pendingCents: number;
  grantedCents: number;
  spentCents: number;
  outstandingCents: number;
  recoveredCents: number;
  unrecoveredCents: number;
};

/** ALL = web and app; WEB / MOBILE = that platform only (order.orderSource). */
export type CashbackPromotionChannel = "ALL" | "WEB" | "MOBILE";

export type AdminCashbackPromotion = {
  id: string;
  name: string;
  channel: CashbackPromotionChannel;
  rateBp: number;
  lotLifetimeDays: number;
  currencyRules: Record<
    string,
    { minimumCents: number; capCents: number | null }
  >;
  startsAt: string;
  endsAt: string;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** One entry per currency; empty before the first award. */
  totals: AdminCashbackPromotionTotals[];
};

/** The candidate that lost under "larger wins" (backend CashbackRunnerUp). */
export type CashbackRunnerUp =
  | { source: "COUPON"; couponId: string | null; couponCode: string; amountCents: number }
  | { source: "PROMOTION"; promotionId: string; promotionName: string | null; amountCents: number };

export type AdminCashbackAward = {
  id: string;
  orderId: string;
  /** Null for a coupon award. */
  promotionId: string | null;
  promotionName: string | null;
  promotionChannel: CashbackPromotionChannel | null;
  source: "PROMOTION" | "COUPON";
  /** Null once the coupon is deleted; couponCode keeps the name. */
  couponId: string | null;
  couponCode: string | null;
  runnerUp: CashbackRunnerUp | null;
  /** The order's own platform, from its orderSource. */
  orderChannel: "WEB" | "MOBILE";
  email: string;
  currency: string;
  terms: Record<string, unknown>;
  placedAt: string;
  qualifyingCents: number;
  amountCents: number;
  entitledCents: number;
  refundedCents: number;
  status: CashbackAwardStatus;
  creditLotId: string | null;
  recoveredCents: number;
  unrecoveredCents: number;
  grantedAt: string | null;
  canceledAt: string | null;
  voidedAt: string | null;
  notifiedAt: string | null;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string | null;
  blockedSince: string | null;
  alertedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lotRemainingCents: number | null;
  lotExpiresAt: string | null;
  lotVoidedAt: string | null;
  spentCents: number;
};

export type AdminCashbackTotals = {
  currency: string;
  awards: number;
  pendingCents: number;
  grantedCents: number;
  outstandingCents: number;
  spentCents: number;
  recoveredCents: number;
  unrecoveredCents: number;
};

export type AdminCashbackAwardList = {
  rows: AdminCashbackAward[];
  total: number;
  totals: AdminCashbackTotals[];
};

export type AdminCashbackAwardsParams = {
  status?: CashbackAwardStatus;
  search?: string;
  promotionId?: string;
  source?: "PROMOTION" | "COUPON";
  /** 1-based, like the server. */
  page?: number;
  limit?: number;
};

export type PromotionWrite = {
  name?: string;
  /** Fixed once the promotion has started (the server answers 409). */
  channel?: CashbackPromotionChannel;
  rateBp?: number;
  lotLifetimeDays?: number;
  currencyRules?: Record<
    string,
    { minimumCents: number; capCents: number | null }
  >;
  startsAt?: string;
  endsAt?: string;
};

/** The award after an admin action, and what the action did. */
export type AdminAwardActionResult = {
  outcome: string;
  award: AdminCashbackAward | null;
};

const ADMIN = `${BASE}/cashback/admin`;

export async function adminListPromotions(): Promise<AdminCashbackPromotion[]> {
  return (await axiosInstance.get(`${ADMIN}/promotions`))
    .data as AdminCashbackPromotion[];
}

export async function adminCreatePromotion(
  body: PromotionWrite,
): Promise<AdminCashbackPromotion> {
  return (await axiosInstance.post(`${ADMIN}/promotions`, body))
    .data as AdminCashbackPromotion;
}

export async function adminUpdatePromotion(
  id: string,
  body: PromotionWrite,
): Promise<AdminCashbackPromotion> {
  return (await axiosInstance.patch(`${ADMIN}/promotions/${id}`, body))
    .data as AdminCashbackPromotion;
}

/** A running promotion ends now; a scheduled one is deleted (`{ deleted: true }`). */
export async function adminEndPromotion(
  id: string,
): Promise<AdminCashbackPromotion | { deleted: true; id: string }> {
  return (await axiosInstance.post(`${ADMIN}/promotions/${id}/end`)).data;
}

export async function adminListAwards(
  params: AdminCashbackAwardsParams,
): Promise<AdminCashbackAwardList> {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
  );
  return (await axiosInstance.get(`${ADMIN}/awards`, { params: clean }))
    .data as AdminCashbackAwardList;
}

/** The order's award, or null. The server wraps it as `{ award }`. */
export async function adminAwardForOrder(
  orderId: string,
): Promise<AdminCashbackAward | null> {
  const data = (await axiosInstance.get(`${ADMIN}/order/${orderId}`)).data as {
    award?: AdminCashbackAward | null;
  };
  return data?.award ?? null;
}

export async function adminSettleAward(
  id: string,
): Promise<AdminAwardActionResult> {
  return (await axiosInstance.post(`${ADMIN}/awards/${id}/settle`))
    .data as AdminAwardActionResult;
}

export async function adminVoidAward(
  id: string,
  reason: string,
): Promise<AdminAwardActionResult> {
  return (await axiosInstance.post(`${ADMIN}/awards/${id}/void`, { reason }))
    .data as AdminAwardActionResult;
}

/** The server's `problems` (400) or `message` (409/404), for an Alert. */
const ERROR_CODES: Record<string, string> = {
  CASHBACK_PROMOTION_OVER: "Akcija je že končana.",
  CASHBACK_PROMOTION_NOT_FOUND: "Akcije ni več.",
  CASHBACK_AWARD_NOT_FOUND: "Vračila ni več.",
};

export function cashbackErrorMessage(err: unknown, fallback: string): string {
  const data = (
    err as {
      response?: {
        data?: { problems?: unknown; message?: unknown; code?: unknown };
      };
    }
  )?.response?.data;
  if (Array.isArray(data?.problems) && data!.problems.length)
    return (data!.problems as string[]).join(" ");
  if (typeof data?.code === "string" && ERROR_CODES[data.code])
    return ERROR_CODES[data.code];
  if (typeof data?.message === "string" && data.message) return data.message;
  if (Array.isArray(data?.message) && data!.message.length)
    return (data!.message as string[]).join(" ");
  if (typeof data?.code === "string") return `${fallback} (${data.code})`;
  return fallback;
}
